import {
  createRecord,
  updateRecord,
  deleteRecord,
  readRecords,
  readRecord,
  getSchemaBySetup,
  readRecordsBySearchFilter,
  getPurchaseOrderDetailsApi,
  batchProcess,
  convertARRecordScheduleType,
  searchRecords,
  getSchemaBySetupForService,
  deleteById,
  getSchemaBySetupForParts,
  getAllSetupListInfo,
  saveUserFavorites,
  getFavoriteSetupsList,
  batchProcessValidate,
} from "../../../api";
import {
  SaveRecordRequest,
  ToastInfo,
  CdcmCudResultObj,
  ReadRecordRequest,
  TableData,
  DeleteRecordRequest,
  RowData,
  RecordsSearchFilterRequest,
  ISelectableOption,
  RecordsSearchFilterResponse,
  JsonSchema,
  CdcmSearchResultObj,
  CdcmResponse,
  SetupBaseRequestInfo,
  SetupListInfoResponse,
  SaveUserFavoritesRequest,
  SaveRecordResponse,
  CdcmSaveResultObject,
  SetupListNavInfoByDepartment,
  NavItem,
} from "../../../types";
import { isNewRecord } from "./rowDataHelpers";
import { MISMATCHED_FUNCTIONAL_UNIT_NAMES, TOAST_MESSAGES } from "../constants";
import { isBFFTypeAheadSearchAvailable } from "../evaluations";
import { TOAST_VARIANTS } from "@cdkglobal/radial";
import { hasValidationErrors } from "./importCOA";

export async function saveRecord(
  request: SaveRecordRequest,
): Promise<SaveRecordResponse> {
  const isNew = request.isNewEntryRecord ?? isNewRecord(request.record);

  const response: CdcmResponse = isNew
    ? await createRecord(request)
    : await updateRecord(request);

  const cudResult = response?.resultObj as CdcmCudResultObj;
  const message = cudResult?.message || (response?.resultObj as string) || "";

  if (response?.resultCode.includes("200")) {
    return {
      variant: "positive",
      message,
      resultObj: response.resultObj as CdcmSaveResultObject,
    };
  }

  return { variant: "negative", message };
}

export async function getTableData(
  request: ReadRecordRequest,
): Promise<TableData> {
  const rowDataList = await getRowDataList(request);
  const schema = await getSchema(request);
  return { rowDataList, schema };
}

export async function deleteRecords(
  request: DeleteRecordRequest,
): Promise<ToastInfo> {
  const response: CdcmResponse = request.recordId
    ? await deleteById(request)
    : await deleteRecord(request);

  const cudResult = response?.resultObj as CdcmCudResultObj;
  const message = cudResult?.message || (response?.resultObj as string[]);

  if (response?.resultCode.includes("200")) {
    return { variant: "positive", message };
  }

  return { variant: "negative", message };
}

export async function getRowDataList(
  request: ReadRecordRequest,
): Promise<Array<RowData>> {
  const response = await readRecords(request);

  if (response.resultCode.includes("200")) {
    return response.resultObj as Array<RowData>;
  }

  return [];
}

export async function getRowDataById(
  request: ReadRecordRequest,
): Promise<RowData> {
  const response = await readRecord(request);
  if (response.resultCode.includes("200")) {
    return response.resultObj as RowData;
  }
  return {};
}

async function getSchema(request: ReadRecordRequest): Promise<JsonSchema> {
  request.functionalUnitName =
    MISMATCHED_FUNCTIONAL_UNIT_NAMES.get(request.functionalUnitName) ??
    request.functionalUnitName;

  const isService = request.functionalArea === "service";
  const isParts = request.functionalArea === "parts";

  if (isService) {
    return await getSchemaBySetupForService(request);
  }
  if (isParts) {
    return await getSchemaBySetupForParts(request);
  }
  return await getSchemaBySetup(request);
}

export async function getRecordsBySearchFilter(
  request: RecordsSearchFilterRequest,
  evalTypeAheadMethods?: string[],
): Promise<{
  options: ISelectableOption[];
  response: RecordsSearchFilterResponse;
}> {
  const response = await fetchRecordsWithFallback(
    request,
    evalTypeAheadMethods,
  );
  const options = mapToSelectableOptions(response, request);
  return { options, response };
}

export async function processBulkUpload(request: SaveRecordRequest): Promise<{
  response: RowData[];
  toastInfo: ToastInfo | null;
  shouldRefetchData: boolean;
}> {
  const response = await batchProcess(request);
  const shouldRefetchData = response?.resultCode.includes("200");

  if (Array.isArray(response.resultObj)) {
    return {
      response: response.resultObj as RowData[],
      toastInfo: null,
      shouldRefetchData,
    };
  }

  const cudResult = response?.resultObj as CdcmCudResultObj;
  const message = cudResult?.message || (response?.resultObj as string) || "";

  return {
    response: [],
    toastInfo: {
      message: message,
      variant: shouldRefetchData ? "positive" : "negative",
    },
    shouldRefetchData,
  };
}

export async function validateBulkUploadRecords(
  request: SaveRecordRequest,
): Promise<{
  response: RowData[];
  toastInfo: ToastInfo;
}> {
  const response = await batchProcessValidate(request);
  if (Array.isArray(response.resultObj)) {
    const hasErrors = hasValidationErrors(response.resultObj as RowData[]);
    return {
      response: response.resultObj as RowData[],
      toastInfo: hasErrors
        ? TOAST_MESSAGES.FILE_VALIDATION_ERROR()
        : TOAST_MESSAGES.FILE_VALIDATION_SUCCESS(),
    };
  }

  const cudResult = response?.resultObj as CdcmCudResultObj;
  const message = cudResult?.message || (response?.resultObj as string) || "";

  return {
    response: [],
    toastInfo: {
      message: message,
      variant: response?.resultCode.includes("200") ? "positive" : "negative",
    },
  };
}

export async function fetchAllSetupListInfo(
  request: SetupBaseRequestInfo,
): Promise<{
  data: SetupListInfoResponse[];
  toastInfo: ToastInfo | null;
}> {
  const response: CdcmResponse = await getAllSetupListInfo(request);
  if (response.resultCode.includes("200")) {
    return {
      data: response.resultObj as SetupListInfoResponse[],
      toastInfo: null,
    };
  }
  return {
    data: [],
    toastInfo: {
      message: response.resultDescription,
      variant: TOAST_VARIANTS.NEGATIVE,
    },
  };
}

export async function fetchUserFavoritesSetups(
  request: SetupBaseRequestInfo,
): Promise<{
  data: string[];
  toastInfo: ToastInfo | null;
}> {
  const response: CdcmResponse = await getFavoriteSetupsList(request);
  if (response.resultCode.includes("200")) {
    return {
      data: Array.isArray(response.resultObj)
        ? (response.resultObj as string[])
        : [],
      toastInfo: null,
    };
  }
  return {
    data: [],
    toastInfo: {
      message: response.resultDescription,
      variant: TOAST_VARIANTS.NEGATIVE,
    },
  };
}

const fetchRecordsWithFallback = async (
  request: RecordsSearchFilterRequest,
  evalTypeAheadMethods?: string[],
): Promise<RecordsSearchFilterResponse> => {
  const isCustomSearch = isBFFTypeAheadSearchAvailable(evalTypeAheadMethods);

  if (isCustomSearch) {
    const cdcmSearchResponse = await searchRecords(request);
    return cdcmSearchResponse.resultObj as CdcmSearchResultObj;
  }

  return await readRecordsBySearchFilter(request);
};

const mapToSelectableOptions = (
  response: RecordsSearchFilterResponse,
  request: RecordsSearchFilterRequest,
): ISelectableOption[] => {
  const isValidResponse =
    Array.isArray(response?.data) &&
    (response?.message === "Record Fetched Successfully" ||
      response?.message === "Records fetched successfully");

  if (!isValidResponse) return [];

  // TODO: Look for possibility to move this to the getSelectableTypeAheadOptions
  if (request.url === "/ROSetups") {
    const data = response.data as {
      grid_names?: { grid_accounting_setup: string }[];
    }[];

    return data.flatMap(
      (record) =>
        record.grid_names?.map((grid) => ({
          label: grid.grid_accounting_setup,
          value: grid.grid_accounting_setup,
        })) ?? [],
    );
  }

  const { valueField, labelField } = request;

  return response.data.map((record) => {
    const label = labelField.map((field) => record[field]).join(" - ");
    const value = Array.isArray(valueField)
      ? valueField.map((field) => record[field]).join(" - ")
      : (record[valueField] as string | number | boolean | null);
    return {
      label,
      value,
    };
  });
};

export const getPurchaseOrderAuthorities = async (): Promise<string[]> => {
  const response = await getPurchaseOrderDetailsApi();
  return response?.iamUser?.authorities?.map((a) => a.authority) || [];
};

export async function updateARScheduleTypeConversionRecord(
  request: SaveRecordRequest,
): Promise<ToastInfo> {
  const response = await convertARRecordScheduleType(request);

  const cudResult = response?.resultObj as CdcmCudResultObj;
  const message = cudResult?.message || (response?.resultObj as string) || "";

  if (response?.resultCode.includes("200")) {
    return { variant: "positive", message };
  }

  return { variant: "negative", message };
}

export async function saveFavorites(
  request: SaveUserFavoritesRequest,
): Promise<ToastInfo> {
  const response = await saveUserFavorites(request);

  const cudResult = response?.resultObj as CdcmCudResultObj;
  const message = cudResult?.message || (response?.resultObj as string) || "";

  if (response?.resultCode.includes("200")) {
    return { variant: "positive", message };
  }

  return { variant: "negative", message };
}

export async function getSetupListInfo({
  setupBaseRequestInfo,
}: {
  setupBaseRequestInfo: SetupBaseRequestInfo;
}): Promise<{
  setupListInfo: SetupListInfoResponse[];
  favoritesList: string[];
  setupListToastInfo: ToastInfo | null;
}> {
  const { data: setupsList, toastInfo } = await fetchAllSetupListInfo({
    ...setupBaseRequestInfo,
    stores: setupBaseRequestInfo.stores,
  });

  if (toastInfo) {
    return {
      setupListInfo: [],
      favoritesList: [],
      setupListToastInfo: toastInfo,
    };
  }

  const { data: favoritesList, toastInfo: favoritesToastInfo } =
    await fetchUserFavoritesSetups(setupBaseRequestInfo);
  if (favoritesToastInfo) {
    return {
      setupListInfo: setupsList,
      favoritesList: [],
      setupListToastInfo: favoritesToastInfo,
    };
  }

  return {
    setupListInfo: setupsList,
    favoritesList,
    setupListToastInfo: favoritesToastInfo,
  };
}

export const fetchFavoritesSetupList = async ({
  setupListNavInfoByDepartment,
  setupBaseRequestInfo,
}: {
  setupListNavInfoByDepartment: SetupListNavInfoByDepartment;
  setupBaseRequestInfo: SetupBaseRequestInfo;
}): Promise<{
  favoritesSetupInfoList: NavItem[];
  toastInfo: ToastInfo | null;
}> => {
  const { data, toastInfo } =
    await fetchUserFavoritesSetups(setupBaseRequestInfo);
  if (toastInfo) {
    return {
      favoritesSetupInfoList: [],
      toastInfo: toastInfo,
    };
  }
  const favoritesSetupInfoList: NavItem[] = Object.values(
    setupListNavInfoByDepartment,
  ).flatMap(({ setups }) =>
    setups.filter(
      (setup) => data.includes(setup.id) && setup.permissions?.hasView,
    ),
  );
  return { favoritesSetupInfoList, toastInfo };
};
